export type SignalStage = 'signals' | 'features' | 'conditions';

export interface SignalChartRow {
  stage: SignalStage;
  family: string;
  key: string;
  targetType: string;
  targetId: string;
  deviceId: string;
  instance: string;
  metricName?: string;
  attributes?: Record<string, unknown> | null;
}

export type ChartBindingMode =
  | 'signal_device'
  | 'signal_device_instance'
  | 'signal_interface'
  | 'feature'
  | 'feature_instance'
  | 'condition'
  | 'condition_instance'
  | 'target'
  | 'device';

export interface RowChartSpec {
  templateKey: string;
  title: string;
  description: string;
  bindingMode: ChartBindingMode;
}

export interface RowChartPlan {
  primary: RowChartSpec;
}

const TOPOLOGY_SIGNAL_KEYS = new Set([
  'routing_bgp_peer_state',
  'routing_ospf_neighbor_state',
  'routing_isis_adjacency_state',
  'routing_bfd_session_state',
  'topology_lag_member',
  'topology_vlan_membership',
  'topology_tunnel_state'
]);

const FEATURE_METRIC_NAMES: Record<string, string> = {
  'interface.unexpected_down': 'promon_interface_unexpected_down',
  'interface.error_rate_5m': 'promon_interface_error_rate_5m',
  'interface.crc_fcs_error_rate_5m': 'promon_interface_crc_fcs_error_rate_5m',
  'interface.alignment_error_rate_5m': 'promon_interface_alignment_error_rate_5m',
  'interface.util_pct_p95_5m': 'promon_interface_util_pct_p95_5m',
  'lag.unexpected_down': 'promon_lag_unexpected_down',
  'lag.members_up_ratio': 'promon_lag_members_up_ratio',
  'bgp.peer_established': 'promon_bgp_peer_established',
  'bgp.reset_rate_15m': 'promon_bgp_reset_rate_15m',
  'device.cpu_pct_p95_5m': 'promon_device_cpu_pct_p95_5m',
  'device.mem_pct_p95_5m': 'promon_device_mem_pct_p95_5m',
  'tunnel.admin_up': 'promon_tunnel_admin_up',
  'tunnel.up': 'promon_tunnel_up',
  'bfd.up': 'promon_bfd_up',
  'bfd.flap_rate_15m': 'promon_bfd_flap_rate_15m',
  'environment.max_temp_celsius': 'promon_environment_max_temp_celsius',
  'environment.failed_fan_count': 'promon_environment_failed_fan_count',
  'environment.psu_redundancy_ratio': 'promon_environment_psu_redundancy_ratio'
};

export const RAW_SIGNAL_IDENTITY_GROUPS: readonly (readonly string[])[] = [
  ['acl_name', 'rule_sequence'],
  ['acl_name', 'rule_name'],
  ['cbqos_object_index'],
  ['peer_addr', 'local_addr'],
  ['peer_address', 'local_address'],
  ['signal_instance'],
  ['peer_addr'],
  ['peer_address'],
  ['vrf_name'],
  ['ent_physical_index'],
  ['pse_group_index'],
  ['channel_if_index'],
  ['component_name'],
  ['component_descr'],
  ['component_kind'],
  ['pse_id'],
  ['port'],
  ['channel'],
  ['class_map_name'],
  ['policy_map_name'],
  ['class'],
  ['policy'],
  ['cbqos_config_index'],
  ['cbqos_policy_index'],
  ['object_type'],
  ['parent_object_index']
];

const rawLabelsForRow = (row: SignalChartRow): Record<string, unknown> => {
  const labels = row.attributes?.labels;
  return labels && typeof labels === 'object' && !Array.isArray(labels) ? (labels as Record<string, unknown>) : {};
};

const hasRawLabel = (labels: Record<string, unknown>, name: string): boolean => {
  const value = labels[name];
  return (typeof value === 'string' && value.trim() !== '') || typeof value === 'number' || typeof value === 'boolean';
};

export const featureMetricNameForKey = (key: string): string | undefined => FEATURE_METRIC_NAMES[key];

export function chartPlanForRow(row: SignalChartRow): RowChartPlan | null {
  if (row.stage === 'signals') {
    if (TOPOLOGY_SIGNAL_KEYS.has(row.key)) {
      return null;
    }
    const labels = rawLabelsForRow(row);
    const hasInterfaceIdentity = hasRawLabel(labels, 'if_name');
    const hasInstanceIdentity = hasRawLabel(labels, 'signal_instance') || hasRawLabel(labels, 'vrf_name');
    const primary: RowChartSpec =
      hasInterfaceIdentity
        ? {
            templateKey: 'site.telemetry.signal_interface_detail',
            title: 'Exact raw signal',
            description: 'Raw metric scoped to the selected interface row.',
            bindingMode: 'signal_interface'
          }
        : hasInstanceIdentity
          ? {
              templateKey: 'site.telemetry.signal_device_instance_detail',
              title: 'Exact raw signal',
              description: 'Raw metric scoped to the selected device and instance row.',
              bindingMode: 'signal_device_instance'
            }
          : {
              templateKey: 'site.telemetry.signal_device_detail',
              title: 'Exact raw signal',
              description: 'Raw metric scoped to the selected device row.',
              bindingMode: 'signal_device'
            };
    return { primary };
  }

  if (row.stage === 'features') {
    const metricName = FEATURE_METRIC_NAMES[row.key];
    if (!metricName) {
      return null;
    }
    const primary: RowChartSpec = row.instance
      ? {
          templateKey: 'site.telemetry.feature_instance_detail',
          title: 'Exact feature signal',
          description: 'Derived feature metric scoped to the selected target and instance row.',
          bindingMode: 'feature_instance'
        }
      : {
          templateKey: 'site.telemetry.feature_detail',
          title: 'Exact feature signal',
          description: 'Derived feature metric scoped to the selected target row.',
          bindingMode: 'feature'
        };
    return { primary };
  }

  const primary: RowChartSpec = row.instance
    ? {
        templateKey: 'site.telemetry.condition_instance_detail',
        title: 'Exact condition state',
        description: 'Condition-state metric scoped to the selected target and instance row.',
        bindingMode: 'condition_instance'
      }
    : {
        templateKey: 'site.telemetry.condition_detail',
        title: 'Exact condition state',
        description: 'Condition-state metric scoped to the selected target row.',
        bindingMode: 'condition'
      };
  return { primary };
}
